import {
  Chart as ChartType,
  LegendItem,
  ChartDataset,
  TooltipModel,
  ChartOptions,
  Plugin,
} from 'chart.js'
import { useTheme } from 'styled-components'

import { MONTHS } from 'src/Constants/constants'

const DASH_VALUE = 5
const TOOLTIP_LINE_SAMPLE_WIDTH = 28
const TOOLTIP_VERTICAL_OFFSET = 20
const TOOLTIP_VIEWPORT_MARGIN = 10
const TOOLTIP_RIGHT_ENHANCED_MARGIN = 20
const TOOLTIP_CENTER_TRANSFORM = 'translate(-50%, 0)'

type TooltipColors = {
  bg: string
  border: string
  titleText: string
  bodyText: string
}

const getOrCreateTooltipEl = (
  chart: ChartType<'line'>,
  colors: TooltipColors,
): HTMLDivElement => {
  const { canvas } = chart
  const container = canvas.parentElement

  if (!container) {
    throw new Error('Chart canvas must have a parent element')
  }

  let el = container.querySelector<HTMLDivElement>('#ext-tooltip')
  if (!el) {
    el = document.createElement('div')
    el.id = 'ext-tooltip'
    el.style.position = 'absolute'
    el.style.pointerEvents = 'none'
    el.style.transform = TOOLTIP_CENTER_TRANSFORM
    el.style.transition = 'all .06s ease'
    el.style.borderRadius = '12px'
    el.style.padding = '12px 14px'
    el.style.boxShadow = '0 4px 12px rgba(0,0,0,.08)'
    el.style.zIndex = '10'
    container.appendChild(el)
  }
  el.style.background = colors.bg
  el.style.border = `1px solid ${colors.border}`
  return el
}

const toDashArray = (dash?: number[] | unknown): string | null => {
  if (Array.isArray(dash) && dash.every((n) => typeof n === 'number')) {
    return dash.join(',')
  }
  return null
}

const lineSampleSVG = (opts: {
  color: string
  width?: number
  strokeWidth?: number
  dashArray?: string | null
}): string => {
  const w = opts.width ?? TOOLTIP_LINE_SAMPLE_WIDTH
  const h = 8
  const y = Math.floor(h / 2)
  const sw = Math.max(1, opts.strokeWidth ?? 2)
  const dash = opts.dashArray ? `stroke-dasharray="${opts.dashArray}"` : ''
  return `
    <svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" style="display:block;">
      <line x1="0" y1="${y}" x2="${w}" y2="${y}" stroke="${opts.color}" stroke-width="${sw}" stroke-linecap="butt" ${dash} />
    </svg>
  `
}

const getBorderWidthAsNumber = (
  borderWidth: ChartDataset<'line', number[]>['borderWidth'],
): number => {
  if (typeof borderWidth === 'number') {
    return borderWidth
  }
  if (Array.isArray(borderWidth)) {
    return borderWidth[0] ?? 2
  }
  return 2
}

const formatDateToTooltipFormat = (dateString: string): string => {
  try {
    const date = new Date(dateString)

    const day = date.getDate().toString().padStart(2, '0')
    const month = MONTHS[date.getMonth()]
    const year = date.getFullYear()

    return `${day} ${month}, ${year}`
  } catch {
    return dateString
  }
}

const generateTooltipTitle = (
  titleLines: string[],
  titleColor: string,
): string => {
  if (!titleLines.length) {
    return ''
  }

  const formattedTitles = titleLines.map((title) =>
    formatDateToTooltipFormat(title),
  )
  return `<div style="font-size:12px;color:${titleColor};font-weight:600;margin-bottom:7px;">${formattedTitles.join('<br/>')}</div>`
}

const generateTooltipDataItem = (
  dp: TooltipModel<'line'>['dataPoints'][0],
  textColor: string,
): string => {
  const ds = dp.dataset as ChartDataset<'line', number[]>
  const color = (ds.borderColor as string) ?? textColor
  const strokeWidth = (ds.borderWidth as number) ?? 2
  const dashArray = toDashArray(ds.borderDash)

  const sample = lineSampleSVG({
    color,
    strokeWidth,
    dashArray,
    width: TOOLTIP_LINE_SAMPLE_WIDTH,
  })

  const label = ds.label ?? ''
  const isCostPerFollower = label.toLowerCase().includes('cost per follower')
  const formattedValue = isCostPerFollower
    ? `$${dp.formattedValue}`
    : dp.formattedValue
  const labelText = `${label}: ${formattedValue}`

  return `
    <div style="display:flex;align-items:center;gap:8px;">
      <div style="width:${TOOLTIP_LINE_SAMPLE_WIDTH}px;flex:0 0 auto">${sample}</div>
      <div style="font-size:13px;color:${textColor};white-space:nowrap;">${labelText}</div>
    </div>`
}

const generateTooltipContent = (
  tooltip: TooltipModel<'line'>,
  colors: TooltipColors,
): string => {
  const titleLines = tooltip.title ?? []
  const bodyItems = tooltip.dataPoints ?? []

  let html = generateTooltipTitle(titleLines, colors.titleText)

  html += '<div style="display:flex;flex-direction:column;gap:1px;">'
  for (const dp of bodyItems) {
    html += generateTooltipDataItem(dp, colors.bodyText)
  }
  html += '</div>'

  return html
}

const positionTooltip = (
  el: HTMLDivElement,
  chart: ChartType<'line'>,
  tooltip: TooltipModel<'line'>,
): void => {
  const hasValidPosition = tooltip.caretX !== null && tooltip.caretY !== null

  if (hasValidPosition) {
    el.style.opacity = '1'

    const { canvas } = chart
    const container = canvas.parentElement

    if (!container) {
      return
    }

    let left = tooltip.caretX
    let top = tooltip.caretY - TOOLTIP_VERTICAL_OFFSET

    const originalOpacity = el.style.opacity
    const originalVisibility = el.style.visibility

    el.style.visibility = 'hidden'
    el.style.opacity = '1'
    el.style.left = '-9999px'
    el.style.top = '-9999px'

    const tooltipRect = el.getBoundingClientRect()
    const tooltipWidth = tooltipRect.width
    const tooltipHeight = tooltipRect.height

    const containerRect = container.getBoundingClientRect()
    const containerWidth = containerRect.width

    const tooltipLeft = left - tooltipWidth / 2
    const tooltipRight = left + tooltipWidth / 2

    if (tooltipLeft < TOOLTIP_VIEWPORT_MARGIN) {
      left = TOOLTIP_VIEWPORT_MARGIN + tooltipWidth / 2
      el.style.transform = TOOLTIP_CENTER_TRANSFORM
    } else if (tooltipRight > containerWidth - TOOLTIP_VIEWPORT_MARGIN) {
      left = containerWidth - TOOLTIP_RIGHT_ENHANCED_MARGIN - tooltipWidth / 2
      el.style.transform = TOOLTIP_CENTER_TRANSFORM
    } else {
      el.style.transform = TOOLTIP_CENTER_TRANSFORM
    }

    if (top < 0) {
      top = tooltip.caretY + TOOLTIP_VERTICAL_OFFSET
    } else if (top + tooltipHeight > containerRect.height) {
      top = tooltip.caretY - tooltipHeight - TOOLTIP_VERTICAL_OFFSET
    } else {
      top = tooltip.caretY - TOOLTIP_VERTICAL_OFFSET
    }

    el.style.visibility = originalVisibility
    el.style.opacity = originalOpacity

    el.style.left = `${left}px`
    el.style.top = `${top}px`
  }
}

const createExternalTooltipHandler =
  (colors: TooltipColors) =>
  (ctx: { chart: ChartType<'line'>; tooltip: TooltipModel<'line'> }): void => {
    const { chart, tooltip } = ctx
    const el = getOrCreateTooltipEl(chart, colors)

    if (tooltip.opacity === 0) {
      el.style.opacity = '0'
      return
    }

    if (tooltip.body) {
      el.innerHTML = generateTooltipContent(tooltip, colors)
    }

    positionTooltip(el, chart, tooltip)
  }

export const usePerformanceChart = (
  strategy?: string,
): { options: ChartOptions<'line'>; legendSpacingPlugin: Plugin<'line'> } => {
  const theme = useTheme()

  const tooltipColors: TooltipColors = {
    bg: theme.color.white,
    border: theme.color.lightGrey,
    titleText: theme.color.darkGrey,
    bodyText: theme.color.darkGrey,
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'center' as const,

        labels: {
          padding: 23,
          usePointStyle: true,
          pointStyle: 'line' as const,
          font: {
            size: 14,
            weight: 400,
          },
          color: theme.color.darkGrey,
          generateLabels(chart: ChartType<'line'>): LegendItem[] {
            const datasets = chart.data.datasets as ChartDataset<
              'line',
              number[]
            >[]
            return datasets.map(
              (ds, i): LegendItem => ({
                text: ds.label ?? `Dataset ${i + 1}`,
                datasetIndex: i,
                hidden: !chart.isDatasetVisible(i),
                fontColor: theme.color.darkGrey,
                strokeStyle: (ds.borderColor as string) ?? theme.color.darkGrey,
                fillStyle: (ds.backgroundColor as string) ?? 'transparent',
                lineWidth: getBorderWidthAsNumber(ds.borderWidth),
                lineDash: (ds.borderDash ?? []) as number[],
                lineCap: (ds.borderCapStyle ?? 'butt') as CanvasLineCap,
                lineJoin: (ds.borderJoinStyle ?? 'miter') as CanvasLineJoin,
                pointStyle: 'line',
              }),
            )
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: createExternalTooltipHandler(tooltipColors),
      },
    },
    layout: {
      padding: {
        top: 0,
        right: -20,
        bottom: 0,
        left: -20,
      },
    },
    scales: {
      y: {
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          color: theme.color.brandGreen,
        },
        border: {
          display: false,
          dash: [DASH_VALUE, DASH_VALUE],
        },
        grid: {
          color: theme.color.lightGrey,
          drawOnChartArea: true,
        },

        ticks: {
          maxTicksLimit: 6,

          color: theme.color.darkGrey,
          font: {
            size: 12,
          },
        },
      },

      y1: {
        display: strategy === 'playlist',
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: true,
          color: theme.color.darkGrey,
        },
        border: {
          display: false,
          dash: [DASH_VALUE, DASH_VALUE],
        },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 5,
          color: theme.color.darkGrey,
          font: {
            size: 12,
          },
          callback(value: number | string): string {
            const formattedNumber = Number(value).toFixed(2)
            return `$${formattedNumber}`
          },
        },
      },
      x: {
        border: {
          display: false,
          dash: [DASH_VALUE, DASH_VALUE],
        },
        grid: {
          color: theme.color.lightGrey,
          drawOnChartArea: true,
        },
        ticks: {
          padding: 10,
          color: theme.color.darkGrey,
          font: {
            size: 12,
          },
          callback(value, index) {
            const { chart } = this
            const { labels } = chart.data
            if (labels && typeof index === 'number' && labels[index]) {
              const label = labels[index] as string
              try {
                const date = new Date(label)
                const day = date.getDate().toString().padStart(2, '0')
                const month = MONTHS[date.getMonth()]
                return `${day} ${month}`
              } catch {
                return String(label)
              }
            }
            return String(value)
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
      line: {
        borderWidth: 1,
      },
    },
  }

  const legendSpacingPlugin = {
    id: 'legendSpacing',
    beforeInit(chart: ChartType<'line'>) {
      if (!chart.legend) {
        return
      }
      const originalFit = chart.legend.fit
      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)()
        this.height += 10
      }
    },
  }

  return { options, legendSpacingPlugin }
}
